import React, { useEffect, useMemo, useState } from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import { getTreeData, getDashboardStats } from "../controllers/dashboard.controller";

import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";

const faNum = (n) => Number(n || 0).toLocaleString("fa-IR");

const buildISOFilters = (filters) => {
  const startISO = filters.startDate ? filters.startDate.toDate().toISOString() : "";

  let endISO = "";
  if (filters.endDate) {
    const d = filters.endDate.toDate();
    d.setHours(23, 59, 59, 999); // inclusive end of day
    endISO = d.toISOString();
  }

  return {
    search: filters.search || "",
    type: filters.type || "",
    startDate: startISO,
    endDate: endISO,
  };
};

const DashboardPage = () => {
  const navigate = useNavigate();

  const today = new Date();
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(today.getDate() - 14);

  const [filters, setFilters] = useState({
    search: "",
    type: "",
    startDate: new DateObject({ date: fourteenDaysAgo, calendar: persian, locale: persian_fa }),
    endDate: new DateObject({ date: today, calendar: persian, locale: persian_fa }),
  });

  const [treeRows, setTreeRows] = useState([]);
  const [stats, setStats] = useState({
    projectsCount: 0,
    clinicsCount: 0,
    pagesCount: 0,
    formsCount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  const typeOptions = useMemo(
    () => [
      { label: "همه", value: "" },
      { label: "آزمایشی", value: "trial" },
      { label: "اصلی", value: "main" },
      { label: "سازمانی", value: "enterprise" },
    ],
    []
  );

  // --- API Calls (با فیلتر مشترک) ---
  const fetchStats = async (params) => {
    try {
      setStatsLoading(true);
      const res = await getDashboardStats(params);
      setStats(res || {});
    } catch (err) {
      console.error("خطا در دریافت آمار:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchTree = async (params) => {
    setLoading(true);
    try {
      const res = await getTreeData(params);

      // بک‌اند ممکنه آرایه بده یا {data: [...]}
      const items = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];

      const idToNodeMap = {};
      const rootNodes = [];

      items.forEach((item) => {
        const id = item.id;
        const parentId = item.parentId;

        const node = {
          key: id,
          data: {
            title: item.title?.fa || item.title?.en || "—",
            type: item.type || "—",
            projectType: item.projectType || "—",
            createdAt: item.createdAt || null,
          },
          children: [],
          leaf: false,
        };

        idToNodeMap[id] = node;

        if (parentId && idToNodeMap[parentId]) {
          idToNodeMap[parentId].children.push(node);
        } else if (!parentId) {
          rootNodes.push(node);
        }
      });

      setTreeRows(rootNodes);
    } catch (err) {
      console.error("خطا در دریافت داده درختی:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyDashboardFilters = async () => {
    const params = buildISOFilters(filters);
    await Promise.all([fetchStats(params), fetchTree(params)]);
  };

  useEffect(() => {
    applyDashboardFilters();
    // eslint-disable-next-line
  }, []);

  // --- Export ---
  const exportToExcel = () => {
    const flatten = (nodes) =>
      nodes.flatMap((n) => [
        {
          title: n.data.title,
          type: n.data.type,
          projectType: n.data.projectType,
          createdAt: n.data.createdAt ? new Date(n.data.createdAt).toLocaleDateString("fa-IR") : "",
        },
        ...flatten(n.children || []),
      ]);

    const flat = flatten(treeRows);
    const worksheet = XLSX.utils.json_to_sheet(flat);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "dashboard.xlsx");
  };

  const exportToPDF = () => {
    const flatten = (nodes) =>
      nodes.flatMap((n) => [
        [
          n.data.title,
          n.data.type,
          n.data.projectType,
          n.data.createdAt ? new Date(n.data.createdAt).toLocaleDateString("fa-IR") : "",
        ],
        ...flatten(n.children || []),
      ]);

    const doc = new jsPDF();
    doc.autoTable({
      head: [["عنوان", "نوع", "نوع پروژه", "تاریخ ایجاد"]],
      body: flatten(treeRows),
      styles: { font: "helvetica", fontSize: 10 },
    });
    doc.save("dashboard.pdf");
  };

  // --- UI helpers ---
  const StatCard = ({ title, value, icon, accent }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        background: `linear-gradient(135deg, ${accent} 0%, rgba(255,255,255,1) 60%)`,
        position: "relative",
        overflow: "hidden",
        minHeight: 96,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -40,
          top: -40,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.45)",
        }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography sx={{ fontWeight: 900, color: "rgba(0,0,0,0.70)" }}>{title}</Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 30, mt: 1 }}>
            {faNum(value)}
          </Typography>
        </Box>
        <Box sx={{ fontSize: 34, opacity: 0.85 }}>{icon}</Box>
      </Stack>
    </Paper>
  );

  return (
    <Box dir="rtl" sx={{ p: 2, background: "#f6f9fc", minHeight: "100vh" }}>
      <style>{`
        /* --- Prime fine tuning --- */
        .p-button { border-radius: 12px !important; font-weight: 800; }
        .p-inputtext { border-radius: 12px !important; height: 42px; }
        .p-dropdown { border-radius: 12px !important; height: 42px; }
        .p-card-clean { border-radius: 18px; border: 1px solid rgba(0,0,0,0.08); background: rgba(255,255,255,0.9); }
        .treetable-wrap .p-treetable { border-radius: 18px; overflow: hidden; border: 1px solid rgba(0,0,0,0.08); }
        .treetable-wrap .p-treetable-thead > tr > th { font-weight: 900; }
      `}</style>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 24 }}>داشبورد مدیریتی</Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
            آمار + فیلتر پیشرفته + خروجی Excel/PDF + ساختار درختی
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Tooltip title="خروجی Excel">
            <Button
              label="خروجی Excel"
              icon="pi pi-file-excel"
              className="p-button-success"
              onClick={exportToExcel}
            />
          </Tooltip>
          <Tooltip title="خروجی PDF">
            <Button
              label="خروجی PDF"
              icon="pi pi-file-pdf"
              className="p-button-danger"
              onClick={exportToPDF}
            />
          </Tooltip>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper className="p-card-clean" elevation={0} sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <span className="p-input-icon-left" style={{ flex: "1 1 320px" }}>
              <i className="pi pi-search" />
              <InputText
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                placeholder="جستجو در عنوان پروژه"
                style={{ width: "100%" }}
              />
            </span>

            <Dropdown
              value={filters.type}
              onChange={(e) => setFilters((p) => ({ ...p, type: e.value }))}
              options={typeOptions}
              placeholder="نوع پروژه"
              style={{ flex: "1 1 220px", width: "100%" }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "flex-end",
                justifyContent: "flex-start",
              }}
            >
              <Box sx={{ minWidth: 190 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 800 }}>
                  از تاریخ
                </Typography>
                <DatePicker
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  value={filters.startDate}
                  onChange={(date) => setFilters((p) => ({ ...p, startDate: date || null }))}
                  style={{ width: "190px", height: 42, borderRadius: 12 }}
                />
              </Box>

              <Box sx={{ minWidth: 190 }}>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 800 }}>
                  تا تاریخ
                </Typography>
                <DatePicker
                  calendar={persian}
                  locale={persian_fa}
                  format="YYYY/MM/DD"
                  value={filters.endDate}
                  onChange={(date) => setFilters((p) => ({ ...p, endDate: date || null }))}
                  style={{ width: "190px", height: 42, borderRadius: 12 }}
                />
              </Box>
            </Box>

            <Button
              label={loading || statsLoading ? "در حال اعمال..." : "اعمال فیلتر"}
              icon="pi pi-filter"
              onClick={applyDashboardFilters}
              disabled={loading || statsLoading}
            />
          </Stack>

          <Divider />

          {/* Active filter chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip
              label={filters.search ? `جستجو: ${filters.search}` : "جستجو: —"}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={
                filters.type
                  ? `نوع پروژه: ${typeOptions.find((x) => x.value === filters.type)?.label || filters.type}`
                  : "نوع پروژه: همه"
              }
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`از: ${filters.startDate ? filters.startDate.format("YYYY/MM/DD") : "—"}`}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`تا: ${filters.endDate ? filters.endDate.format("YYYY/MM/DD") : "—"}`}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <StatCard title="پروژه‌ها" value={stats?.projectsCount} icon="💼" accent="rgba(33,150,243,0.16)" />
        <StatCard title="کلینیک‌ها" value={stats?.clinicsCount} icon="🏥" accent="rgba(76,175,80,0.16)" />
        <StatCard title="صفحات" value={stats?.pagesCount} icon="📄" accent="rgba(255,152,0,0.16)" />
        <StatCard title="فرم‌ها" value={stats?.formsCount} icon="🧾" accent="rgba(156,39,176,0.14)" />
      </Box>

      {/* TreeTable */}
      <Paper className="p-card-clean treetable-wrap" elevation={0} sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, pb: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>ساختار پروژه‌ها</Typography>
          <Typography sx={{ color: "text.secondary", fontWeight: 800 }}>
            نتایج مطابق فیلترهای بالا
          </Typography>
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ProgressSpinner />
            <Typography sx={{ mt: 2, color: "text.secondary", fontWeight: 800 }}>
              در حال دریافت داده‌ها...
            </Typography>
          </Box>
        ) : (
          <TreeTable value={treeRows} tableStyle={{ minWidth: "100%" }}>
            <Column field="title" header="عنوان" expander />
            <Column field="type" header="نوع" />
            <Column field="projectType" header="نوع پروژه" />
            <Column
              field="createdAt"
              header="تاریخ ایجاد"
              body={(row) => (row?.data?.createdAt ? new Date(row.data.createdAt).toLocaleDateString("fa-IR") : "—")}
            />
            <Column
              header="عملیات"
              body={(rowData) =>
                rowData?.data?.type === "Project" ? (
                  <Button
                    icon="pi pi-sitemap"
                    className="p-button-sm p-button-outlined p-button-primary"
                    label="مدیریت پروژه"
                    onClick={() => navigate(`/hierarchy?projectId=${String(rowData.key).replace("project-", "")}`)}
                  />
                ) : (
                  <Typography sx={{ color: "text.secondary" }}>—</Typography>
                )
              }
            />
          </TreeTable>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardPage;
